import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { AuthModal, type AuthMode } from "./AuthModal";

interface OpenOpts {
  mode?: AuthMode;
  /** Called once after a successful sign-in (e.g. replay the gated action). */
  onSuccess?: () => void;
}

interface AuthModalContextValue {
  open: (opts?: OpenOpts) => void;
  close: () => void;
  isOpen: boolean;
}

const Ctx = createContext<AuthModalContextValue | null>(null);

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<AuthMode>("login");
  const onSuccessRef = useRef<(() => void) | undefined>(undefined);

  const open = useCallback((opts?: OpenOpts) => {
    onSuccessRef.current = opts?.onSuccess;
    setMode(opts?.mode ?? "login");
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const handleSuccess = useCallback(() => {
    setIsOpen(false);
    const cb = onSuccessRef.current;
    onSuccessRef.current = undefined;
    cb?.();
  }, []);

  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <AuthModal open={isOpen} mode={mode} onOpenChange={setIsOpen} onModeChange={setMode} onSuccess={handleSuccess} />
    </Ctx.Provider>
  );
}

export function useAuthModal(): AuthModalContextValue {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuthModal must be used within <AuthModalProvider>");
  return c;
}
